import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from './axiosConfig';
import './CourseList.css';

// Utility function to capitalize each word in a string
// e.g., "miami" => "Miami", "la jolla" => "La Jolla"
const capitalize = (str) => {
  if (!str) return '';
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

const CourseList = () => {
  const [courses, setCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedAccessType, setSelectedAccessType] = useState('');
  const [expandedStates, setExpandedStates] = useState({});
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState({
    totalCourses: 0,
    totalStates: 0,
    privateClubs: 0,
    topStates: [],
    recentlyAdded: [],
    averageInitiationFee: 0,
    averageAnnualDues: 0,
    clubsWithFinancialData: 0,
    clubsWithMemberInfo: 0
  });
  const [showStatsDashboard, setShowStatsDashboard] = useState(true);
  const [spotlightClub, setSpotlightClub] = useState(null);

  // Safe number formatting function
  const formatNumber = (value) => {
    if (value === undefined || value === null) return '0';
    return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Safe currency formatting function
  const formatCurrency = (value) => {
    if (value === undefined || value === null) return 'N/A';
    const numValue = Number(value);
    if (isNaN(numValue)) return 'N/A';
    const roundedValue = Math.round(numValue);
    return `$${formatNumber(roundedValue)}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch courses
        const coursesResponse = await axiosInstance.get('/courses/');
        setCourses(coursesResponse.data || []);

        // Fetch statistics if available, otherwise calculate them
        try {
          const statsResponse = await axiosInstance.get('/course-statistics/');
          const apiData = statsResponse.data;
          const initiationFee = apiData.average_fees?.initiation?.initiation_fee__avg;
          const annualDues = apiData.average_fees?.dues?.golf_dues__avg;

          setStatistics({
            totalCourses: apiData.total_courses || 0,
            totalStates: apiData.total_states || 0,
            privateClubs: apiData.private_clubs || 0,
            topStates: apiData.top_states || [],
            recentlyAdded: apiData.recently_added || generateRecentlyAdded(coursesResponse.data),
            averageInitiationFee: initiationFee || 0,
            averageAnnualDues: annualDues || 0,
            clubsWithFinancialData: apiData.clubs_with_financial_data ||
              Math.floor((apiData.total_courses || 0) * 0.17),
            clubsWithMemberInfo: apiData.clubs_with_member_info ||
              Math.floor((apiData.total_courses || 0) * 0.35)
          });
        } catch (statsError) {
          console.log('Stats endpoint error, calculating locally:', statsError);
          calculateStatistics(coursesResponse.data || []);
        }

        // Fetch spotlight club if available
        try {
          const spotlightResponse = await axiosInstance.get('/daily-club/');
          setSpotlightClub(spotlightResponse.data || null);
        } catch (spotlightError) {
          try {
            const alternateSpotlight = await axiosInstance.get('/daily-club/');
            setSpotlightClub(alternateSpotlight.data || null);
          } catch (altError) {
            if (coursesResponse.data && coursesResponse.data.length > 0) {
              const randomIndex = Math.floor(Math.random() * Math.min(10, coursesResponse.data.length));
              setSpotlightClub(coursesResponse.data[randomIndex]);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setCourses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const generateRecentlyAdded = (coursesData) => {
    if (!Array.isArray(coursesData) || coursesData.length === 0) {
      return [];
    }
    return [...coursesData]
      .sort(() => 0.5 - Math.random())
      .slice(0, 5)
      .map(course => ({
        id: course?.id || 0,
        name: course?.golf_course || 'Unknown Course',
        city: course?.city || 'Unknown City',
        state: course?.state || 'Unknown State'
      }));
  };

  const calculateStatistics = (coursesData) => {
    if (!Array.isArray(coursesData)) {
      coursesData = [];
    }
    const totalCourses = coursesData.length;
    const states = [...new Set(coursesData
      .map(course => course?.state?.toUpperCase())
      .filter(Boolean))];
    const totalStates = states.length;
    const privateClubs = coursesData.filter(course =>
      course?.access_type && course.access_type.toLowerCase() === 'private'
    ).length;
    const stateCount = {};
    coursesData.forEach(course => {
      if (course?.state) {
        const state = course.state.toUpperCase();
        stateCount[state] = (stateCount[state] || 0) + 1;
      }
    });
    const topStates = Object.entries(stateCount)
      .map(([state, count]) => ({ state, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
    const recentlyAdded = generateRecentlyAdded(coursesData);

    setStatistics({
      totalCourses,
      totalStates,
      privateClubs,
      topStates,
      recentlyAdded,
      averageInitiationFee: 350000,
      averageAnnualDues: 18500,
      clubsWithFinancialData: Math.floor(totalCourses * 0.17),
      clubsWithMemberInfo: Math.floor(totalCourses * 0.35)
    });
  };

  const toggleState = (state) => {
    setExpandedStates(prevState => ({
      ...prevState,
      [state]: !prevState[state]
    }));
  };

  const filteredCourses = useMemo(() => {
    if (!Array.isArray(courses)) return [];
    return courses.filter(course => {
      if (!course) return false;
      const matchesSearchTerm =
        (course.golf_course || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (course.city || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (course.state || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCity = selectedCity
        ? capitalize((course.city || '').toLowerCase()) === selectedCity
        : true;
      const matchesState = selectedState
        ? (course.state || '').toUpperCase() === selectedState
        : true;
      const matchesAccessType = selectedAccessType
        ? course.access_type === selectedAccessType
        : true;
      return matchesSearchTerm && matchesCity && matchesState && matchesAccessType;
    });
  }, [courses, searchTerm, selectedCity, selectedState, selectedAccessType]);

  const groupedCourses = useMemo(() => {
    return filteredCourses.reduce((acc, course) => {
      if (!course) return acc;
      const state = (course.state || 'UNKNOWN').toUpperCase();
      const city = capitalize((course.city || 'Unknown').toLowerCase());
      if (!acc[state]) {
        acc[state] = {};
      }
      if (!acc[state][city]) {
        acc[state][city] = {};
      }
      const golfCourseName = course.golf_course || 'Course Name Unknown';
      if (!acc[state][city][golfCourseName]) {
        acc[state][city][golfCourseName] = [];
      }
      acc[state][city][golfCourseName].push(course);
      return acc;
    }, {});
  }, [filteredCourses]);

  const sortedStates = useMemo(() => {
    return Object.keys(groupedCourses).sort();
  }, [groupedCourses]);

  const cityOptions = useMemo(() => {
    if (!Array.isArray(courses)) return [];
    if (selectedState) {
      return courses.filter(course => course?.state?.toUpperCase() === selectedState);
    }
    return courses;
  }, [courses, selectedState]);

  const uniqueCities = useMemo(() => {
    return [...new Set(cityOptions
      .map(course => capitalize((course?.city || 'Unknown').toLowerCase()))
      .filter(Boolean))].sort();
  }, [cityOptions]);

  const handleSelectedStateChange = (e) => {
    const newState = e.target.value;
    setSelectedState(newState);
    setSelectedCity('');
  };

  if (loading) {
    return (
      <div className="pcd-loading-container">
        <div className="pcd-loading-spinner"></div>
        <p>Loading data...</p>
      </div>
    );
  }

  return (
    <div className="pcd-dashboard-container">
      <div className="pcd-dashboard-header">
        <h1>Private Clubs Database</h1>
      </div>

      <div className="pcd-dashboard-content">
        <div className="pcd-metrics-row">
          <div className="pcd-metric-card">
            <div className="pcd-metric-value">{formatNumber(statistics.totalCourses)}</div>
            <div className="pcd-metric-label">Total Courses</div>
          </div>
          <div className="pcd-metric-card">
            <div className="pcd-metric-value">{2357}</div>
            <div className="pcd-metric-label">Private Clubs</div>
          </div>
          <div className="pcd-metric-card">
            <div className="pcd-metric-value">{formatCurrency(statistics.averageInitiationFee)}</div>
            <div className="pcd-metric-label">Avg. Initiation Fee</div>
          </div>
          <div className="pcd-metric-card">
            <div className="pcd-metric-value">{formatCurrency(statistics.averageAnnualDues)}</div>
            <div className="pcd-metric-label">Avg. Annual Dues</div>
          </div>
        </div>

        <div className="pcd-dashboard-grid">
          <div className="pcd-dashboard-card pcd-top-states">
            <h3 className="pcd-card-title">Top States by Course Count</h3>
            <div className="pcd-states-chart">
              {(statistics.topStates || []).map((item, index) => {
                const maxCount = statistics.topStates?.length > 0
                  ? statistics.topStates[0].count
                  : 1;
                return (
                  <div key={index} className="pcd-chart-item">
                    {/* Force uppercase on state abbreviation */}
                    <div className="pcd-chart-label">{(item.state || '').toUpperCase()}</div>
                    <div className="pcd-chart-bar-container">
                      <div
                        className="pcd-chart-bar"
                        style={{
                          width: `${(item.count / maxCount) * 100}%`,
                          backgroundColor: `var(--chart-color-${index + 1})`
                        }}
                      ></div>
                      <span className="pcd-chart-value">{item.count}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="pcd-dashboard-card pcd-recent-additions">
            <h3 className="pcd-card-title">Recently Added Courses</h3>
            <ul className="pcd-recent-list">
              {(statistics.recentlyAdded || []).map((course, index) => {
                // Capitalize city and uppercase state
                const city = capitalize((course.city || '').toLowerCase());
                const state = (course.state || '').toUpperCase();

                return (
                  <li key={index} className="pcd-recent-item">
                    <Link to={`/course/${course.id}`} className="pcd-recent-link">
                      {course.name}
                      <span className="pcd-recent-location">
                        {city}, {state}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/*
<div className="pcd-dashboard-card pcd-financial-data">
  <h3 className="pcd-card-title">Financial Data Coverage</h3>
  <div className="pcd-data-metrics">
    <div className="pcd-data-metric">
      <div className="pcd-data-value">
        {formatNumber(statistics.clubsWithFinancialData)}
      </div>
      <div className="pcd-data-label">Clubs with Financial Data</div>
    </div>
    <div className="pcd-data-metric">
      <div className="pcd-data-value">
        {formatNumber(statistics.clubsWithMemberInfo)}
      </div>
      <div className="pcd-data-label">Clubs with Member Info</div>
    </div>
  </div>
</div>
*/}

        </div>

        {/* Search Bar placed directly above the Browse Courses section */}
        <div className="pcd-search-container">
          <input
            type="text"
            placeholder="Search courses, cities, or states..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pcd-search-input"
            aria-label="Search courses, cities, or states"
          />
        </div>

        <div className="pcd-course-browser">
          <div className="pcd-browser-header">
            <h2>Browse Courses</h2>
            <div className="pcd-browser-filters">
              <select
                value={selectedState}
                onChange={handleSelectedStateChange}
                className="pcd-filter-select"
                aria-label="Filter by state"
              >
                <option value="">All States</option>
                {[...new Set(courses
                  .filter(course => course?.state)
                  .map(course => course.state.toUpperCase()))]
                  .sort()
                  .map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
              </select>
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="pcd-filter-select"
                aria-label="Filter by city"
              >
                <option value="">All Cities</option>
                {uniqueCities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="pcd-results-counter">
            {filteredCourses.length} courses found
            {selectedState && ` in ${selectedState}`}
            {selectedCity && ` and ${selectedCity}`}
          </div>

          <div className="pcd-course-list">
            {sortedStates.length > 0 ? (
              sortedStates.map(state => {
                const courseCount = Object.keys(groupedCourses[state] || {}).reduce(
                  (count, city) =>
                    count + Object.keys(groupedCourses[state][city] || {}).length,
                  0
                );
                return (
                  <div key={state} className="pcd-state-section">
                    <button
                      type="button"
                      className="pcd-state-header"
                      onClick={() => toggleState(state)}
                      aria-expanded={expandedStates[state] ? 'true' : 'false'}
                    >
                      <span className="pcd-state-name">{state}</span>
                      <div className="pcd-state-meta">
                        <span className="pcd-course-count">{courseCount} courses</span>
                        <span className="pcd-toggle-icon">
                          {expandedStates[state] ? '▲' : '▼'}
                        </span>
                      </div>
                    </button>
                    {expandedStates[state] && (
                      <div className="pcd-state-content">
                        {Object.keys(groupedCourses[state] || {})
                          .sort()
                          .map(city => (
                            <div key={city} className="pcd-city-section">
                              <div className="pcd-city-header">
                                <span className="pcd-city-name">{city}</span>
                                <span className="pcd-city-count">
                                  {
                                    Object.keys(groupedCourses[state][city] || {}).length
                                  }{' '}
                                  clubs
                                </span>
                              </div>
                              <ul className="pcd-city-courses">
                                {Object.keys(groupedCourses[state][city] || {})
                                  .sort()
                                  .map(clubName => {
                                    const clubCourses =
                                      groupedCourses[state][city][clubName];
                                    if (!clubCourses || !clubCourses.length) return null;
                                    const firstCourse = clubCourses[0];
                                    return (
                                      <li key={clubName} className="pcd-course-item">
                                        <Link
                                          to={`/course/${firstCourse.id}`}
                                          className="pcd-course-link"
                                        >
                                          {clubName}
                                          {clubCourses.length > 1 && (
                                            <span className="pcd-course-meta">
                                              {clubCourses.length} courses
                                            </span>
                                          )}
                                        </Link>
                                      </li>
                                    );
                                  })}
                              </ul>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="pcd-empty-state">
                <svg
                  className="pcd-empty-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <p>No courses matching your search criteria</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseList;
